import {
  GridColDef,
  GridColumnGroup,
  GridColumnGroupingModel,
  GridColumnNode,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import {Button} from '@wandb/weave/components/Button';
import {ErrorBoundary} from '@wandb/weave/components/ErrorBoundary';
import {LoadingDots} from '@wandb/weave/components/LoadingDots';
import {CellValue} from '@wandb/weave/components/PagePanelComponents/Home/Browse2/CellValue';
import {OnUpdateFilter} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/filters/CellFilterWrapper';
import {CellFilterWrapper} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/filters/CellFilterWrapper';
import {NotApplicable} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/NotApplicable';
import {EXPANDED_REF_REF_KEY} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/tsDataModelHooksCallRefExpansion';
import {SmallRef} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/smallRef/SmallRef';
import {CustomWeaveTypeProjectContext} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/typeViews/CustomWeaveTypeDispatcher';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import {parseRef} from '@wandb/weave/react';
import React from 'react';

import {buildTreeWithDepth, CollapsedPathInfo} from './buildTreeWithDepth';

const isExpandedRefWithValueAsTableRef = (val: any): boolean => {
  return (
    typeof val === 'object' &&
    val !== null &&
    EXPANDED_REF_REF_KEY in val &&
    typeof val[EXPANDED_REF_REF_KEY] === 'string'
  );
};

/**
 * Determines if a group node should show an ellipsis indicator
 * based on the path mapping and header depth configuration.
 */
const shouldShowGroupEllipsis = (
  key: string,
  keyParts: string[],
  pathMapping: Map<string, CollapsedPathInfo>,
  maxHeaderDepth: number
): {shouldShow: boolean; pathInfo?: CollapsedPathInfo} => {
  try {
    for (const [fullPath, info] of pathMapping.entries()) {
      const collapsedParts = info.collapsedPath.split('.');
      const groupMatchesCollapsed =
        collapsedParts.slice(0, keyParts.length).join('.') === key;

      if (groupMatchesCollapsed && info.hiddenSegments.length > 0) {
        const currentDepth = keyParts.length;

        // For maxDepth >= 3, ellipsis appears at the second visible level
        if (maxHeaderDepth >= 3 && currentDepth === 2) {
          const originalParts = fullPath.split('.');
          const positionInOriginal = originalParts.indexOf(
            keyParts[keyParts.length - 1]
          );

          if (positionInOriginal > currentDepth - 1) {
            const pathUpToCurrentLevel = originalParts
              .slice(0, positionInOriginal + 1)
              .join('.');
            return {
              shouldShow: true,
              pathInfo: {
                fullPath: pathUpToCurrentLevel,
                collapsedPath: info.collapsedPath,
                hiddenSegments: info.hiddenSegments,
              },
            };
          }
        }
      }
    }
  } catch (error) {
    console.error('Error in shouldShowGroupEllipsis:', error);
  }
  return {shouldShow: false};
};

/**
 * Determines if a leaf column should show an ellipsis indicator
 * based on the path mapping and header depth configuration.
 */
const getLeafEllipsisInfo = (
  pathInfo: CollapsedPathInfo | undefined,
  maxHeaderDepth: number
): {shouldShow: boolean; hiddenSegments: string[]} => {
  try {
    if (pathInfo && pathInfo.hiddenSegments.length > 0) {
      // For maxDepth=1 or 2, ellipsis appears at the leaf level
      if (maxHeaderDepth === 1 || maxHeaderDepth === 2) {
        return {
          shouldShow: true,
          hiddenSegments: pathInfo.hiddenSegments,
        };
      }
    }
  } catch (error) {
    console.error('Error in getLeafEllipsisInfo:', error);
  }
  return {shouldShow: false, hiddenSegments: []};
};

interface CollapseHeaderProps {
  headerName: string;
  field: string;
  onCollapse: (field: string) => void;
  pathInfo?: CollapsedPathInfo;
}

const CollapseHeader: React.FC<CollapseHeaderProps> = ({
  headerName,
  field,
  onCollapse,
  pathInfo,
}) => {
  const content = (
    <div className="flex items-center gap-1">
      {pathInfo && <span>...</span>}
      <div className="flex-1">{headerName}</div>
      <Button
        icon="expand-right"
        variant="secondary"
        size="small"
        className="ml-2 rotate-180"
        onClick={() => onCollapse(field)}
        tooltip="Collapse column"
      />
    </div>
  );

  return pathInfo ? (
    <Tooltip
      content={<div className="font-mono text-xs">{pathInfo.fullPath}</div>}
      side="bottom"
      trigger={content}
    />
  ) : (
    content
  );
};

interface ExpandHeaderProps {
  headerName: string;
  field: string;
  hasExpand: boolean;
  onExpand: (field: string) => void;
  pathInfo?: CollapsedPathInfo;
}

const ExpandHeader: React.FC<ExpandHeaderProps> = ({
  headerName,
  field,
  hasExpand,
  onExpand,
  pathInfo,
}) => {
  const content = (
    <div className="flex items-center gap-1">
      {pathInfo && <span>...</span>}
      <div className="flex-1">{headerName}</div>
      {hasExpand && (
        <Button
          icon="expand-right"
          variant="ghost"
          size="small"
          className="ml-2"
          onClick={() => onExpand(field)}
          tooltip="Expand column"
        />
      )}
    </div>
  );

  return pathInfo ? (
    <Tooltip
      content={<div className="font-mono text-xs">{pathInfo.fullPath}</div>}
      side="bottom"
      trigger={content}
    />
  ) : (
    content
  );
};

/**
 * Utility function to recursively walk and transform a grouping model tree
 */
const walkGroupingModel = (
  nodes: GridColumnNode[],
  fn: (node: GridColumnNode) => GridColumnNode
) => {
  return nodes.map(node => {
    node = fn(node);
    if ('children' in node) {
      node.children = walkGroupingModel(node.children, fn);
    }
    return node;
  });
};

/**
 * Cache type for tree building to avoid expensive recalculations
 */
export type TreeBuildingCache = Map<
  string,
  {tree: GridColumnGroup; pathMapping: Map<string, CollapsedPathInfo>}
>;

/**
 * Memoized tree building function that caches results based on column names and depth
 */
const buildTreeMemoized = (
  columnNames: string[],
  maxHeaderDepth: number,
  cache: TreeBuildingCache
): {tree: GridColumnGroup; pathMapping: Map<string, CollapsedPathInfo>} => {
  try {
    const cacheKey = `${JSON.stringify(columnNames.slice().sort())}_${maxHeaderDepth}`;

    if (cache.has(cacheKey)) {
      return cache.get(cacheKey)!;
    }

    const pathMapping = new Map<string, CollapsedPathInfo>();
    const tree = buildTreeWithDepth([...columnNames], {
      maxDepth: maxHeaderDepth,
      pathMapping,
    });

    const result = {tree, pathMapping};

    // Keep cache size reasonable - remove oldest entries if too large
    if (cache.size > 10) {
      const firstKey = cache.keys().next().value;
      if (firstKey !== undefined) {
        cache.delete(firstKey);
      }
    }

    cache.set(cacheKey, result);
    return result;
  } catch (error) {
    console.error('Error in buildTreeMemoized:', error);
    // Return a fallback simple tree structure
    return {
      tree: {groupId: '', children: []},
      pathMapping: new Map(),
    };
  }
};

export const buildDynamicColumnsWithDepth = <T extends GridValidRowModel>(
  filteredDynamicColumnNames: string[],
  entityProjectFromRow: (row: T) => {entity: string; project: string},
  valueForKey: (row: T, key: string) => any,
  cache: TreeBuildingCache,
  columnIsExpanded?: (col: string) => boolean,
  columnCanBeExpanded?: (col: string) => boolean,
  onCollapse?: (col: string) => void,
  onExpand?: (col: string) => void,
  columnIsSortable?: (col: string) => boolean,
  onUpdateFilter?: OnUpdateFilter,
  maxHeaderDepth: number = 2,
  ioLoading: boolean = false
) => {
  const cols: Array<GridColDef<T>> = [];

  // Validate inputs
  if (!Array.isArray(filteredDynamicColumnNames)) {
    console.error(
      'buildDynamicColumnsWithDepth: filteredDynamicColumnNames must be an array'
    );
    return {cols: [], groupingModel: []};
  }

  if (maxHeaderDepth < 1 || maxHeaderDepth > 20) {
    console.error(
      'buildDynamicColumnsWithDepth: maxHeaderDepth must be between 1 and 20'
    );
    return {cols: [], groupingModel: []};
  }

  let tree: GridColumnGroup;
  let pathMapping: Map<string, CollapsedPathInfo>;

  try {
    const result = buildTreeMemoized(
      filteredDynamicColumnNames,
      maxHeaderDepth,
      cache
    );
    tree = result.tree;
    pathMapping = result.pathMapping;
  } catch (error) {
    console.error('Error building tree structure:', error);
    // Fallback to simple columns without grouping
    return {
      cols: filteredDynamicColumnNames.map(key => ({
        field: key,
        headerName: key.split('.').pop() || key,
        flex: 1,
        minWidth: 150,
      })),
      groupingModel: [],
    };
  }

  let groupingModel: GridColumnGroupingModel = tree.children.filter(
    (c): c is GridColumnGroup => 'groupId' in c
  );

  const groupIds = new Set<string>();
  groupingModel = walkGroupingModel(groupingModel, node => {
    if ('groupId' in node) {
      const key = node.groupId;
      groupIds.add(key);
      const keyParts = key.split('.');

      const {shouldShow: shouldShowEllipsis, pathInfo: effectivePathInfo} =
        shouldShowGroupEllipsis(key, keyParts, pathMapping, maxHeaderDepth);

      const isExpanded =
        columnIsExpanded && onCollapse && columnIsExpanded(key);
      const canExpand =
        columnCanBeExpanded && onExpand && columnCanBeExpanded(key);

      if (isExpanded || canExpand) {
        node.renderHeaderGroup = () => {
          const headerName = keyParts.slice(-1)[0];
          const pathInfo = shouldShowEllipsis ? effectivePathInfo : undefined;

          if (isExpanded) {
            return (
              <CollapseHeader
                headerName={headerName}
                field={key}
                onCollapse={onCollapse}
                pathInfo={pathInfo}
              />
            );
          } else if (onExpand) {
            return (
              <ExpandHeader
                headerName={headerName}
                field={key}
                hasExpand
                onExpand={onExpand!}
                pathInfo={pathInfo}
              />
            );
          }
          return <div>{headerName}</div>;
        };
      } else if (shouldShowEllipsis && effectivePathInfo) {
        // Even if not expandable/collapsible, show the ellipsis if this is the truncation point
        node.renderHeaderGroup = () => {
          const content = (
            <div className="flex items-center gap-1">
              <span>...</span>
              <div>{node.headerName}</div>
            </div>
          );

          return (
            <Tooltip
              content={
                <div className="font-mono text-xs">
                  {effectivePathInfo!.fullPath}
                </div>
              }
              side="bottom"
              trigger={content}
            />
          );
        };
      }
    }
    return node;
  }) as GridColumnGroupingModel;

  for (const key of filteredDynamicColumnNames) {
    try {
      const pathInfo = pathMapping.get(key);
      const keyParts = key.split('.');

      const {
        shouldShow: shouldShowLeafEllipsis,
        hiddenSegments: leafHiddenSegments,
      } = getLeafEllipsisInfo(pathInfo, maxHeaderDepth);

      const col: GridColDef<T> = {
        flex: 1,
        minWidth: 150,
        field: key,
        sortable: columnIsSortable && columnIsSortable(key),
        headerName: key,
        display: 'flex',
        renderHeader: () => {
          const headerContent = keyParts.slice(-1)[0];

          // Show ellipsis only if this leaf should have it
          if (shouldShowLeafEllipsis && leafHiddenSegments.length > 0) {
            const content = (
              <div
                className="flex items-center gap-1 w-full"
                style={{display: 'flex', alignItems: 'center'}}>
                <span>...</span>
                <span>{headerContent}</span>
              </div>
            );

            return (
              <Tooltip
                content={<div className="font-mono text-xs">{key}</div>}
                side="bottom"
                trigger={content}
              />
            );
          }
          return <div>{headerContent}</div>;
        },
        valueGetter: (_unused: any, row: any) => {
          const val = valueForKey(row, key);
          if (Array.isArray(val) || typeof val === 'object') {
            try {
              return JSON.stringify(val);
            } catch {
              return val;
            }
          }
          return val;
        },
        renderCell: cellParams => {
          const {entity, project} = entityProjectFromRow(cellParams.row);

          const val = valueForKey(cellParams.row, key);
          if (val === undefined) {
            if (ioLoading) {
              return <LoadingDots />;
            }
            return (
              <CellFilterWrapper
                onUpdateFilter={onUpdateFilter}
                field={key}
                rowId={cellParams.id.toString()}
                operation={'(any): isEmpty'}
                value={undefined}>
                <NotApplicable />
              </CellFilterWrapper>
            );
          }
          return (
            <ErrorBoundary>
              <CellFilterWrapper
                onUpdateFilter={onUpdateFilter}
                field={key}
                rowId={cellParams.id.toString()}
                operation={null}
                value={val}
                style={{
                  width: '100%',
                  height: '100%',
                  alignContent: 'center',
                }}>
                {isExpandedRefWithValueAsTableRef(val) ? (
                  <SmallRef objRef={parseRef(val[EXPANDED_REF_REF_KEY])} />
                ) : (
                  <CustomWeaveTypeProjectContext.Provider
                    value={{entity, project}}>
                    <CellValue value={val} field={key.split('.').pop() ?? ''} />
                  </CustomWeaveTypeProjectContext.Provider>
                )}
              </CellFilterWrapper>
            </ErrorBoundary>
          );
        },
      };

      const isExpandedColumn =
        columnIsExpanded && onCollapse && columnIsExpanded(key);
      const canExpandColumn =
        columnCanBeExpanded && onExpand && columnCanBeExpanded(key);

      if (isExpandedColumn || canExpandColumn) {
        col.renderHeader = () => {
          const calculatedPathInfo =
            shouldShowLeafEllipsis && leafHiddenSegments.length > 0
              ? {
                  fullPath: key,
                  collapsedPath: pathInfo?.collapsedPath || '',
                  hiddenSegments: leafHiddenSegments,
                }
              : undefined;

          const headerName = keyParts[keyParts.length - 1];

          if (isExpandedColumn) {
            const expansionRepresentedByGroup = groupIds.has(col.field);
            if (expansionRepresentedByGroup) {
              // In the case where we have an expanded column AND that column
              // is a group, then the group itself will handle the expansion
              // and title presentation. Maybe we want to display the title
              // or "Ref" here?
              return <></>;
            }
            return (
              <CollapseHeader
                headerName={headerName}
                field={key}
                onCollapse={onCollapse}
                pathInfo={calculatedPathInfo}
              />
            );
          } else if (onExpand) {
            return (
              <ExpandHeader
                headerName={headerName}
                field={key}
                hasExpand
                onExpand={onExpand!}
                pathInfo={calculatedPathInfo}
              />
            );
          }
          return <div>{headerName}</div>;
        };
      }
      cols.push(col);
    } catch (error) {
      console.error(`Error creating column for key "${key}":`, error);
      // Add a fallback simple column
      cols.push({
        field: key,
        headerName: key.split('.').pop() || key,
        flex: 1,
        minWidth: 150,
        valueGetter: (_unused: any, row: any) => {
          try {
            return valueForKey(row, key);
          } catch {
            return 'Error';
          }
        },
        renderCell: () => <div>Error loading column</div>,
      });
    }
  }

  return {cols, groupingModel};
};
